import Vue from 'vue';
import slugify from 'slugify';
import uuidv4 from 'uuid/v4';
import ColorThief from 'colorthief';
import { thumbHashToDataURL, thumbHashToRGBA, rgbaToThumbHash } from 'thumbhash';
import { customAlphabet } from 'nanoid';
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)
import Painterro from 'painterro';
import { sneakyPromise } from './profiling';


export const APPROVE_VIEW_TIME = 10;
export const NOTIFY_HIDE_TIME = 20;

window.utils = {
  verbalPriority: (task) => {
    const priority = ['Low', 'Normal', 'High', 'Highest', 'Extra'];
    return priority[task.priority + 1];
  },
  triggers: () => {
    const triggers = {
      startTrack: 'Start tracking',
      assigneeChanged: 'Task reassigned',
      assigneeTransfered: 'Reassigned in tracker',
      done: 'Done pressed',
      pause: 'Pause pressed',
    };
    return triggers;
  },
  actions: () => {
    const actions = {
      moveTask: {
        name: 'Move task to',
        target: 'stages',
      },
      moveBack: {
        name: 'Move back',
        target: null,
      },
      assign: {
        name: 'Assign to',
        target: 'users',
      },
      unassign: {
        name: 'Unassign',
        target: null,
      },
    };
    return actions;
  },
  ruleMeta: () => [
    {
      id: 'creator',
      name: 'Creator',
      type: 'user',
    },
    {
      id: 'assignee',
      name: 'Last executor',
      type: 'user',
    },
    {
      id: 'stage',
      name: 'Last stage',
      type: 'stage',
    },
    {
      id: 'time',
      name: 'Tracked time',
      type: 'time',
    },
  ],
  ruleOperators: () => [
    {
      id: 'is',
      name: 'Is',
      comparator: (a, b) => a === b,
    },
    {
      id: 'isnot',
      name: 'Is not',
      comparator: (a, b) => a !== b,
    },
    {
      id: 'less',
      name: 'Less than',
      comparator: (a, b) => a < b,
    },
    {
      id: 'more',
      name: 'More than',
      comparator: (a, b) => a > b,
    },
    {
      id: 'userBefore',
      name: 'Assigned before',
    },
  ],
  genId: () => uuidv4(),
  rgbToHex: (r, g, b) => `#${[r, g, b].map((x) => {
    const hex = x.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  }).join('')}`,
  getColorOfImageTag: tag => sneakyPromise((resolve) => {
    if (!tag.width) {
      return;
    }
    const colorThief = new ColorThief();
    const rgba = colorThief.getColor(tag, 5);
    if (!rgba) {
      resolve('rgba(0,0,0,0)'); // seems to be happen on white image
    } else {
      resolve(window.utils.rgbToHex(rgba[0], rgba[1], rgba[2]));
    }
  }),
  getColorOfB64: b64str => sneakyPromise(async (resolve) => {
    const img = document.createElement('img');
    await sneakyPromise((r) => {
      img.src = b64str;
      img.onload = r;
    });
    const colorThief = new ColorThief();
    let rgba = null;
    try {
      rgba = colorThief.getColor(img, 5);
    } catch (e) {
      // crashes e.g. on animated gif
      console.warn("Can't get bg color")
    }
    if (!rgba) {
      resolve('rgba(0,0,0,0)'); // seems to be happen on white image
    } else {
      resolve(window.utils.rgbToHex(rgba[0], rgba[1], rgba[2]));
    }
  }),
};


export function callREST(method, url, data = {}, token, resolving = false) {
  let dstURL;
  const node = 'node1';
  if (resolving) {
    dstURL = url;
  } else if (process.env.VUE_APP_ENV === 'live') {
    dstURL = `https://${node}.tracklify.com${url}`;
  } else if (process.env.VUE_APP_ENV === 'dev') {
    dstURL = `https://${node}.devtracklify.com${url}`;
  } else if (process.env.VUE_APP_ENV === 'local') {
    dstURL = `http://localhost:3001${url}`;
  }
  console.log('callREST dst URL *** ', dstURL);


  return Promise.race(
    [
      sneakyPromise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10 * 1000)),
      sneakyPromise((resolve) => {
        fetch(dstURL, {
          method,
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            Authorization: `Bearer ${token}`,
          },
          redirect: 'follow',
          referrer: 'no-referrer',
          body: JSON.stringify(data),
        }).then((response) => {
          response.json().then((d) => {
            resolve(d);
          });
        });
      }),
    ],
  );
}

export async function nodeResolver(workspaceObj) {
  let url = 'https://tracklify.com/rapi/getWorkspaceNode';

  if (process.env.VUE_APP_ENV === 'dev') {
    url = 'https://devtracklify.com/rapi/getWorkspaceNode';
  }

  const res = await callREST(
    'POST',
    url,
    workspaceObj,
    null,
    true,
  );
  return res;
}

export function notifyError(error) {
  // todo
  console.error(error);
}

export function findNextTabStop(el) {
  const all = document.querySelectorAll('input, button, select, textarea, a[href]');
  const list = Array.prototype.filter.call(all, item => item.tabIndex >= '0');
  const index = list.indexOf(el);
  return list[index + 1] || list[0];
}

// the hex ThumbHash attachments are stored with: the image fitted into 100×100, never enlarged
export async function imageToThumbHash(blob) {
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, 100 / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const { data } = context.getImageData(0, 0, width, height);
  return Array.from(rgbaToThumbHash(width, height, data), b => b.toString(16).padStart(2, '0')).join('');
}

export function thumbHashToBytes(thumbHash) {
  if (!thumbHash || thumbHash.length % 2 !== 0) {
    return null;
  }
  const bytes = new Uint8Array(thumbHash.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(thumbHash.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

// a task card and its picture both show the same preview placeholder, and boards re-render cards: decode each once
const thumbHashDataUrls = new Map();

export function thumbHashToImageDataUrl(thumbHash) {
  if (thumbHashDataUrls.has(thumbHash)) {
    return thumbHashDataUrls.get(thumbHash);
  }
  const bytes = thumbHashToBytes(thumbHash);
  if (!bytes) {
    return '';
  }
  try {
    const dataUrl = thumbHashToDataURL(bytes);
    thumbHashDataUrls.set(thumbHash, dataUrl);
    return dataUrl;
  } catch (error) {
    console.error('Failed to decode ThumbHash', { thumbHash, error });
    return '';
  }
}

export function calculateDominantBrightness(thumbHash) {
  const bytes = thumbHashToBytes(thumbHash);
  if (!bytes) {
    return 0;
  }
  let decoded;
  try {
    decoded = thumbHashToRGBA(bytes);
  } catch (error) {
    console.error('Failed to calculate ThumbHash brightness', { thumbHash, error });
    return 0;
  }
  const pixels = [...decoded.rgba];
  let arrays = [];
  while (pixels.length > 3) {
    arrays.push(pixels.splice(0, 4));
  }
  arrays = arrays.filter((_, i) => i % 4 === 0);
  return arrays.reduce((acc, arr) => {
    const [rC, gC, bC] = arr;
    return acc + (0.2126 * rC / 255.0 + 0.7152 * gC / 255.0 + 0.0722 * bC / 255.0);
  }, 0) / arrays.length;
}

export function fileIconByExt(ext) {
  let icon = 'file';
  if (['bz2', 'gz', 'zip', '.jar', '7z', 'rar'].includes(ext)) {
    icon = 'file-archive';
  } else if (['mp3', 'wav', 'ogg', 'midi'].includes(ext)) {
    icon = 'file-audio';
  } else if (['mpeg', 'mpg', 'avi', 'qt'].includes(ext)) {
    icon = 'file-movie';
  } else if (['txt', 'md', 'log', 'plain'].includes(ext)) {
    icon = 'file-alt';
  } else if (['rtf', 'doc', 'docx', 'odt', 'dot'].includes(ext)) {
    icon = 'file-word';
  } else if (['py', 'html', 'pl', 'php', 'js',
    'xml', 'css', 'url', 'htm', 'sql', 'sh'].includes(ext)) {
    icon = 'file-code';
  } else if (['xls', 'xlt', 'xlm', 'xlsx', 'xlsm', 'xltx', 'xltm'].includes(ext)) {
    icon = 'file-excel';
  } else if (['pdf'].includes(ext)) {
    icon = 'file-pdf';
  } else if (['ppt', 'odp', 'pot', 'pps', 'potx', 'ppsx', 'pptx'].includes(ext)) {
    icon = 'file-powerpoint';
  } else if (['csv'].includes(ext)) {
    icon = 'file-csv';
  }
  return icon;
}

export const timeouter = () => new Promise((_, reject) => {
  setTimeout(() => {
    reject(new Error('Timeout'));
  }, 5000);
});


export const getScreenDevices = async () => {
  const { desktopCapturer } = window.APP_TRON;

  let sources = null;
  try {
    const before = new Date();
    sources = await Promise.race([desktopCapturer.getSources({ types: ['screen'] }), timeouter()]);
    console.log('desktopCapturer.getSources time is', (new Date() - before) / 1e3);
  } catch (e) {
    throw new Error(`Can't get the screens: ${e}<end>`);
  }
  const fullScreen = sources.find(s => s.name.toLowerCase() === 'Entire Screen'.toLowerCase());
  const screenIds = [];
  if (fullScreen) {
    // Entire screen source is present
    screenIds.push(fullScreen.id);
  } else {
    screenIds.push(...sources.map(s => s.id));
  }
  return screenIds;
};
export const initSwipes = async () => {
  // if (!window.devicePlatformIsMobile) {
  //   return;
  // }
  // todo: uncomment, added to debug in devtools (IDK how to setup UA in devtools, did it, but it is not working)
  let xDown = null;
  let yDown = null;
  function getTouches(evt) {
    return evt.touches // browser API
      || evt.originalEvent.touches; // jQuery
  }
  function handleTouchStart(evt) {
    const firstTouch = getTouches(evt)[0];
    xDown = firstTouch.clientX;
    yDown = firstTouch.clientY;
  }
  function handleTouchMove(evt) {
    if (!xDown || !yDown) {
      return;
    }
    const xUp = evt.touches[0].clientX;
    const yUp = evt.touches[0].clientY;
    const xDiff = xDown - xUp;
    const yDiff = yDown - yUp;
    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0 && window.onRightSwipe) {
        /* right swipe */
        window.onRightSwipe();
      } else if (xDiff < 0 && window.onLeftSwipe) {
        /* left swipe */
        window.onLeftSwipe();
      }
    } else if (yDiff > 0 && window.onDownSwipe) {
      /* down swipe */
      window.onDownSwipe();
    } else if (yDiff < 0 && window.onUpSwipe) {
      /* up swipe */
      window.onUpSwipe();
    }
    xDown = null;
    yDown = null;
  }
  document.addEventListener('touchstart', handleTouchStart, false);
  document.addEventListener('touchmove', handleTouchMove, false);
};

export const getTronDisplayMedia = async (options = {}) => {
  const { desktopCapturer } = window.APP_TRON;
  const sourceTypes = options.sourceTypes || ['screen', 'window'];
  const thumbnailSize = options.thumbnailSize;
  const audio = options.audio ?? false;
  const video = options.video || {};
  const videoMandatory = video.mandatory || {};

  // copied from https://github.com/electron/electron/issues/16513#issuecomment-602070250
  return sneakyPromise(async (resolve, reject) => {
    try {
      const getSourcesStartedAt = Date.now();
      console.log('🖥️ getTronDisplayMedia:getSources start', {
        sourceTypes,
        thumbnailSize,
        startedAt: new Date(getSourcesStartedAt).toISOString(),
      });
      const sources = await desktopCapturer.getSources({
        types: sourceTypes,
        ...(thumbnailSize ? { thumbnailSize } : {}),
      });
      console.log('🖥️ getTronDisplayMedia:getSources resolved', {
        sourceTypes,
        thumbnailSize,
        durationMs: Date.now() - getSourcesStartedAt,
        count: sources?.length,
        resolvedAt: new Date().toISOString(),
      });
      const getDesktopCaptureStream = async (sourceId) => window.navigator.mediaDevices.getUserMedia({
        audio,
        video: {
          mandatory: {
            ...videoMandatory,
            chromeMediaSource: 'desktop',
            chromeMediaSourceId: sourceId,
          },
        },
      });

      if (!sources.length) {
        reject(new Error('No desktop capture sources available'));
        return;
      }

      if (sources.length === 1) {
        const getUserMediaStartedAt = Date.now();
        console.log('🖥️ getTronDisplayMedia:getUserMedia start (auto single source)', {
          sourceId: sources[0].id,
          startedAt: new Date(getUserMediaStartedAt).toISOString(),
        });
        const stream = await getDesktopCaptureStream(sources[0].id);
        console.log('🖥️ getTronDisplayMedia:getUserMedia resolved (auto single source)', {
          sourceId: sources[0].id,
          durationMs: Date.now() - getUserMediaStartedAt,
          resolvedAt: new Date().toISOString(),
        });
        resolve(stream);
        return;
      }

      const selectionElem = document.createElement('div');
      selectionElem.classList = 'desktop-capturer-selection';
      const sourcesList = sources.map(({ id, name, thumbnail }) => {
        let screenView;
        try {
          screenView = thumbnail.dataUrl;
        } catch (e) {
          console.error('error getting display preview', e);
          console.log('thumbnail', typeof thumbnail, thumbnail);

          // sometime it drops Illegal invocation: Function must be called on an object of type NativeImage
          screenView = '';
        }
        return `<li class="desktop-capturer-selection__item">
          <button class="desktop-capturer-selection__btn" data-id="${id}" title="${name}">
            <img class="desktop-capturer-selection__thumbnail" src="${screenView}" />
            <span class="desktop-capturer-selection__name">${name}</span>
          </button>
        </li>`;
      }).join('');

      // add close button so user can cancel selection; wrapper kept simple to avoid extra nesting
      selectionElem.innerHTML = `
        <div class="desktop-capturer-selection__panel">
          <button class="desktop-capturer-selection__close" type="button" title="Close" aria-label="Close">&times;</button>
          <div class="desktop-capturer-selection__scroller">
            <ul class="desktop-capturer-selection__list">
              ${sourcesList}
            </ul>
          </div>
        </div>
      `;
      document.body.appendChild(selectionElem);
      let finished = false; // prevent multiple resolve/reject
      const closeBtn = selectionElem.querySelector('.desktop-capturer-selection__close');

      const cleanup = () => {
        // remove esc listener & element
        document.removeEventListener('keydown', escHandler, true);
        try { selectionElem.remove(); } catch (e) { /* ignore */ }
      };

      const cancel = (reason = 'Desktop capture selection cancelled by user') => {
        if (finished) return;
        finished = true;
        cleanup();
        reject(new Error(reason));
      };

      function escHandler(ev) {
        if (ev.key === 'Escape' || ev.code === 'Escape') {
          // highest priority cancel, stop everything else
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
          cancel('Desktop capture selection cancelled by user (esc)');
        }
      }
      // capture phase to intercept before app shortcuts
      document.addEventListener('keydown', escHandler, true);

      if (closeBtn) {
        closeBtn.addEventListener('click', () => cancel());
      }
      document.querySelectorAll('.desktop-capturer-selection__btn')
        .forEach((button) => {
          button.addEventListener('click', async () => {
            try {
              const id = button.getAttribute('data-id');
              const source = sources.find(src => src.id === id);
              if (!source) {
                throw new Error(`Source with id ${id} does not exist`);
              }
              const getUserMediaStartedAt = Date.now();
              console.log('🖥️ getTronDisplayMedia:getUserMedia start (manual pick)', {
                sourceId: source.id,
                startedAt: new Date(getUserMediaStartedAt).toISOString(),
              });
              const stream = await getDesktopCaptureStream(source.id);
              console.log('🖥️ getTronDisplayMedia:getUserMedia resolved (manual pick)', {
                sourceId: source.id,
                durationMs: Date.now() - getUserMediaStartedAt,
                resolvedAt: new Date().toISOString(),
              });
              if (!finished) {
                finished = true;
                cleanup();
                resolve(stream);
              }
            } catch (err) {
              console.error('Error selecting desktop capture source:', err);
              cancel(err.message || 'Error selecting desktop capture source');
            }
          });
        });
    } catch (err) {
      console.error('Error displaying desktop capture sources:', err);
      reject(err);
    }
  });
};

export const generateHash = (length = 8) => {
  const nanoid = customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', length);
  const hash = nanoid();
  return hash;
};

// !!!!!!!!!!!
// if you change anything inside this function, please change it on both frontend and backend version also!!
// desync of function behaviors will lead to broken ids on newly created projects
//
// TODO: move this to commonUtils.js and make symlink between frontend and backend!! 
export const beautySlug = (data, slugWhenEmpty) => {
  let str = data.text || '';

  const sliceLength = data.sliceLength || 5;

  if (str?.includes(' ')) {
    str = str.split(' ').map(x => x.slice(0, sliceLength)).join('-');
  }

  str = slugify(str, {
    lower: data.lower || false,
  });

  return str.substring(0, data.length || 20) || slugWhenEmpty || 'untitled';
};



export const attachmentUrl = attachment => `${window.s3base}/attachments/${attachment.workspaceId}/${attachment.projectId}/`
    + `${attachment.taskId}/${attachment.id}.${attachment.extension}`;

export function editImageWithPainterro({
  imageUrl, onSave, onClose
}) {
  window.painterroOpenedInstance = Painterro({
    toolbarHeightPx: 54,
    hiddenTools: ['redo', 'bucket'],
    colorScheme: {
      main: '#E8F1F3', // make panels light-yellow
      control: '#4D96A3', // change controls color
      controlContent: '#ffffff',
      activeControl: '#A35956',
      activeControlContent: '#ffffff',
      hoverControl: '#6CCADB',
    },
    defaultArrowLength: 15,
    availableArrowLengths: [10, 15, 20, 30],
    hideByEsc: true,
    saveByEnter: true,
    onClose: () => {
      console.log('🐕‍🦺 Painterro closed');
      window.painterroOpenedInstance = null;
      if (onClose) {
        onClose();
      }
    },
    saveHandler: async (imageData, done) => {
      await onSave(imageData);
      done(true);
      window.painterroOpenedInstance = null;
    },
  }).show(imageUrl);
}


(() => {
  const originalLog = console.log;
  const start = performance.now();

  function getColor(elapsedSeconds) {
    if (elapsedSeconds < 1) {
      const green = 255;
      const red = Math.round(255 * elapsedSeconds);
      return `rgb(${red}, ${green}, 0)`;
    } else if (elapsedSeconds < 3) {
      const red = 255;
      const green = Math.round(255 * (1 - (elapsedSeconds - 1) / 2));
      return `rgb(${red}, ${green}, 0)`;
    } else {
      return 'rgb(255, 0, 0)';
    }
  }

  console.log = function (...args) {
    const elapsed = (performance.now() - start) / 1000;
    const timeText = `${elapsed.toFixed(3)}s`;
    const color = getColor(elapsed);
    const style = `font-size: 10px; color: ${color};`;
    originalLog.call(console, `%c${timeText} %c${args[0]}`, style, '', ...args.slice(1))
  };
})();

export class AsyncQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
  }

  async add(task) {
    this.queue.push(task);
    if (!this.processing) {
      this.process();
    }
  }

  async process() {
    this.processing = true;
    while (this.queue.length > 0) {
      const task = this.queue.shift();
      try {
        await task();
      } catch (error) {
        console.error('Task encountered an error:', error);
      }
    }
    this.processing = false;
  }
}



export function checkWhetherMainWindow() {
  const location = window.location.href;
  return !(
    location.includes('#/approveWindow') ||
    location.includes('#/notifyWindow') ||
    location.includes('#/notifyDownTime') ||
    location.includes('#/resumeWindow') ||
    location.includes('#/taskNotifyWindow') ||
    location.includes('#/stillHereWindow')
  );
}


export async function readFileAsDataURL(file) {
  // do faster cleanup on GC
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    // Safety timeout - reject if reading takes more than 10 seconds
    const timeout = setTimeout(() => {
      reader.onload = null; // cleanup
      reader.onerror = null; // cleanup
      reject(new Error('File reading timeout after 10 seconds'));
    }, 10000);
    
    reader.onload = () => {
      clearTimeout(timeout);
      reader.onload = null; // cleanup
      reader.onerror = null; // cleanup
      resolve(reader.result);
    };
    reader.onerror = (e) => {
      clearTimeout(timeout);
      reader.onload = null; // cleanup
      reader.onerror = null; // cleanup
      reject(e);
    }
    reader.readAsDataURL(file);
  });
}

export async function dataURLtoFile(dataUrl, filename) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], filename, { type: blob.type });
}

export function saveTaskToLocalStorage(task) {
  const taskNoAttachments = {
    ...task,
    attachments: [], // attachment might be too large for localStorage
    screenshots: [],  // screenshots might be too large for localStorage
  };
  localStorage.setItem('LAST_TASK', JSON.stringify(taskNoAttachments));
}

// The last project of the current workspace, loaded from scratch (after the workspace changed, or when the open
// project is not accessible). The app's window never navigates: the hash route changes in place and the main
// process reloads it.
export function openLastProjectOfCurrentWorkspace() {
  if (window.APP_TRON) {
    // replaceState leaves the router as it is: the window shows the new route only after the reload
    window.history.replaceState(null, '', '#/project/last/Last');
    window.APP_TRON.ipcRenderer.send('update-application');
    return;
  }
  document.location.href = '/project/last/Last';
}

// the last tracked task when it is of the workspace open now: another workspace's task can't be tracked here
export function lastTrackedTaskOfOpenWorkspace() {
  const lastTask = JSON.parse(localStorage.getItem('LAST_TASK'));
  const openWorkspaceId = document.cookie.match(/(?:^|; )_tracklify_workspace=([^;]*)/)?.[1];

  return lastTask?.workspaceId === openWorkspaceId ? lastTask : null;
}
